import { useState, useRef, useEffect, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage, resetSessionCache } from "@/lib/queryClient";
import { consumeReturnTo, consumeLifetimeReturnTo } from "@/lib/auth-return-to";
import { useToast } from "@/hooks/use-toast";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Loader2, ShieldCheck, ArrowLeft, KeyRound } from "lucide-react";
import logoImg from "@assets/Padpulse_logo_horizontal_nobg.png";
import { applyThemeClass } from "@/lib/theme";
import { usePageTitle } from "@/hooks/use-page-title";

interface VerifyResponse {
  success: boolean;
  pendingInvitationCount?: number;
  user: {
    id: number;
    email: string;
    name: string | null;
    approved: boolean;
    status?: string;
    phone?: string | null;
    onboardingCompletedAt?: string | null;
    onboardingWizardCompletedAt?: string | null;
    preferredTheme?: string;
  };
}

export default function AppTotpVerify() {
  usePageTitle("Two-Factor Auth");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [backupCode, setBackupCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const [sessionProbeState, setSessionProbeState] = useState<"checking" | "ready">("checking");
  const probingRef = useRef(false);
  const navigatedRef = useRef(false);

  const probeSession = useCallback(async () => {
    if (probingRef.current || navigatedRef.current) return;
    probingRef.current = true;
    try {
      const res = await fetch("/api/app/profile", { credentials: "include" });
      if (res.ok && !navigatedRef.current) {
        navigatedRef.current = true;
        const profile = await res.json().catch(() => null);
        resetSessionCache();
        navigate(consumeReturnTo(profile?.id ?? null) ?? "/app/dashboard");
        return;
      }
    } catch {
      // Network error — fall through to show the form
    } finally {
      probingRef.current = false;
    }
    setSessionProbeState("ready");
  }, [navigate]);

  useEffect(() => {
    probeSession();
  }, [probeSession]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        probeSession();
      }
    };
    const handleFocus = () => {
      probeSession();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [probeSession]);

  const verifyMutation = useMutation<VerifyResponse, Error, string>({
    mutationFn: async (totpCode: string) => {
      const res = await apiRequest("POST", "/api/app/auth/totp/verify", { totpCode });
      return res.json() as Promise<VerifyResponse>;
    },
    onSuccess: (data) => {
      setCodeError(null);
      resetSessionCache();
      const u = data?.user;
      const lifetimeReturn = consumeLifetimeReturnTo(u?.id ?? null);
      if (lifetimeReturn) { navigate(lifetimeReturn); return; }
      if (u?.preferredTheme === "dark" || u?.preferredTheme === "light" || u?.preferredTheme === "system") {
        applyThemeClass(u.preferredTheme);
      }
      if (u?.status === "waitlist") {
        navigate("/app/awaiting-approval");
      } else if (!u?.phone) {
        navigate("/app/onboarding");
      } else if (!u?.onboardingWizardCompletedAt) {
        // Task #1630 — Gate on the wizard-finished flag.
        navigate("/app/welcome");
      } else if ((data?.pendingInvitationCount ?? 0) > 0) {
        navigate("/app/accept-invitations");
      } else {
        // Phase 3 — only the dashboard-default branch honors a stashed deep
        // link; every onboarding/approval branch above keeps precedence. The
        // stash is bound to the user it was captured for.
        navigate(consumeReturnTo(data?.user?.id ?? null) ?? "/app/dashboard");
      }
    },
    onError: (err: Error) => {
      const message = parseErrorMessage(err, "Invalid code. Please try again.");
      setCodeError(message);
      toast({ title: "Invalid code", description: message, variant: "destructive" });
      if (!useBackup) {
        setCode(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    },
  });

  const handleChange = (index: number, value: string) => {
    // A multi-character value means a password manager (1Password) or browser
    // autofill dropped the whole code into one box without firing a paste
    // event. Strip any separators (e.g. "123 456") and distribute the digits
    // across all boxes so autofill fills the whole code.
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, 6);
      if (!digits) return;
      if (codeError) setCodeError(null);
      const newCode = ["", "", "", "", "", ""];
      for (let i = 0; i < 6; i++) newCode[i] = digits[i] || "";
      setCode(newCode);
      inputRefs.current[Math.min(digits.length, 5)]?.focus();
      if (digits.length === 6 && !verifyMutation.isPending) {
        verifyMutation.mutate(digits);
      }
      return;
    }
    // A single typed character (or an empty string from deletion): accept only
    // digits or a clear; ignore stray non-digit keystrokes.
    if (value && !/^\d$/.test(value)) return;
    if (codeError) setCodeError(null);
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    const fullCode = newCode.join("");
    if (fullCode.length === 6 && !verifyMutation.isPending) {
      verifyMutation.mutate(fullCode);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    if (codeError) setCodeError(null);
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < 6; i++) {
      newCode[i] = pasted[i] || "";
    }
    setCode(newCode);
    if (pasted.length === 6) {
      verifyMutation.mutate(pasted);
    }
  };

  const handleBackupSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!backupCode.trim()) return;
    verifyMutation.mutate(backupCode.trim());
  };

  if (sessionProbeState === "checking") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center" data-testid="totp-verify-session-checking">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 bg-blue-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-orange-100/30 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img src={logoImg} alt="PadPulse" className="h-[50px] w-auto" data-testid="img-totp-logo" />
        </div>

        <Card className="w-full p-8 shadow-xl border border-gray-100 bg-white/80 backdrop-blur-sm">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-brand" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-1 text-center" data-testid="text-totp-title">
            Two-Factor Authentication
          </h1>
          <p className="text-gray-500 text-sm mb-6 text-center" data-testid="text-totp-subtitle">
            {useBackup
              ? "Enter one of your backup codes to sign in."
              : "Open your authenticator app and enter the 6-digit code."}
          </p>

          {!useBackup ? (
            <>
              <div className="flex gap-2.5 justify-center mb-2" onPaste={handlePaste}>
                {code.map((digit, i) => (
                  <Input
                    key={i}
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    className={`w-12 h-14 text-center text-2xl font-bold ${codeError ? "border-red-500" : "border-gray-200 focus:border-brand"}`}
                    autoFocus={i === 0}
                    data-testid={`input-totp-${i}`}
                  />
                ))}
              </div>
              {codeError && (
                <p className="text-xs text-red-600 mb-4 text-center" data-testid="error-totp-code">{codeError}</p>
              )}
              {!codeError && <div className="mb-4" />}
              {verifyMutation.isPending && (
                <div className="flex justify-center mb-4">
                  <Loader2 className="h-6 w-6 animate-spin text-brand" />
                </div>
              )}
            </>
          ) : (
            <form onSubmit={handleBackupSubmit} className="mb-4">
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Backup code</label>
                <Input
                  type="text"
                  placeholder="XXXX-XXXX"
                  value={backupCode}
                  onChange={(e) => { setBackupCode(e.target.value); if (codeError) setCodeError(null); }}
                  className={`font-mono uppercase ${codeError ? "border-red-500" : "border-gray-200"}`}
                  autoFocus
                  data-testid="input-backup-code"
                />
                {codeError && (
                  <p className="text-xs text-red-600 mt-1" data-testid="error-backup-code">{codeError}</p>
                )}
              </div>
              <Button
                type="submit"
                className="w-full bg-brand hover:bg-brand-hover text-white font-semibold"
                disabled={verifyMutation.isPending}
                data-testid="button-backup-submit"
              >
                {verifyMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify Backup Code"}
              </Button>
            </form>
          )}

          <div className="flex flex-col items-center gap-2 pt-2 border-t border-gray-100">
            <button
              onClick={() => { setUseBackup(!useBackup); setCodeError(null); setCode(["", "", "", "", "", ""]); setBackupCode(""); }}
              className="text-sm text-brand hover:underline flex items-center gap-1"
              data-testid="button-toggle-backup"
            >
              <KeyRound className="h-3.5 w-3.5" />
              {useBackup ? "Use authenticator app instead" : "Use a backup code instead"}
            </button>
            <Button variant="ghost" size="sm" asChild className="text-gray-500 hover:text-gray-700" data-testid="button-back-totp-login">
              <Link href="/app/login"><ArrowLeft className="h-4 w-4 mr-1" /> Back to login</Link>
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
